import type {
  AccountDateRequest,
  AccountDateRequestsMeta,
} from '~/composables/types/account-date-request'

const DATE_REQUESTS_INCLUDE =
  'offerDates,eventDescription,eventDescription.address.locality,eventDescription.media,eventDescription.pricing'

export const useAccountDateRequests = () => {
  const { getAccessToken } = useAuthTokens()

  const dateRequests = useState<AccountDateRequest[]>(
    'accountDateRequests',
    () => []
  )
  const currentPage = useState('accountDateRequestsPage', () => 1)
  const totalPages = useState('accountDateRequestsTotalPages', () => 1)
  const totalCount = useState('accountDateRequestsTotalCount', () => 0)
  const isLoading = useState('accountDateRequestsLoading', () => false)
  const error = useState<Error | null>('accountDateRequestsError', () => null)

  const dateRequestCount = computed(
    () => totalCount.value || dateRequests.value.length
  )

  const hasMore = computed(() => currentPage.value < totalPages.value)

  async function fetchDateRequests({
    page = 1,
    append = false,
  }: {
    page?: number
    append?: boolean
  } = {}) {
    isLoading.value = true
    error.value = null

    try {
      const token = await getAccessToken()
      const response = await kftApiRequest<
        AccountDateRequest[],
        AccountDateRequestsMeta
      >('/v1/store/date-requests', {
        method: 'GET',
        query: {
          orderBy: 'id',
          sortedBy: 'desc',
          page,
          limit: 15,
          include: DATE_REQUESTS_INCLUDE,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      const nextItems = response.data ?? []
      dateRequests.value = append
        ? [...dateRequests.value, ...nextItems]
        : nextItems
      currentPage.value = response.meta?.pagination?.current_page ?? page
      totalPages.value = response.meta?.pagination?.total_pages ?? 1
      totalCount.value =
        response.meta?.pagination?.total ?? dateRequests.value.length
    } catch (err) {
      console.error('Fetch date requests error', err)
      error.value = err instanceof Error ? err : new Error('Unknown error')

      if (!append) {
        dateRequests.value = []
        totalCount.value = 0
      }

      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function loadMoreDateRequests() {
    if (!hasMore.value || isLoading.value) {
      return
    }

    await fetchDateRequests({
      page: currentPage.value + 1,
      append: true,
    })
  }

  return {
    dateRequests,
    dateRequestCount,
    isLoading,
    error,
    hasMore,
    fetchDateRequests,
    loadMoreDateRequests,
  }
}
